import { useState, useEffect } from "react";
import { Send, Loader2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { loc } from "@/lib/localize";
import { trackEvent } from "@/lib/analytics";
import { validatePersonName, validateMessageText, PHONE_PATTERN } from "@/lib/validation";

export default function InquiryForm() {
  const { t, lang } = useLang();
  const { content } = useContent();
  const [params] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: params.get("service") || "",
    destination_country: params.get("country") || "",
    message: "",
    website: "", // honeypot — left blank by real visitors, must stay out of tab order/view
  });

  useEffect(() => {
    setForm((f) => ({
      ...f,
      service_type: params.get("service") || f.service_type,
      destination_country: params.get("country") || f.destination_country,
    }));
  }, [params]);

  const services = content?.services || [];
  const countries = [
    ...(content?.countries_primary || []),
    ...(content?.countries_secondary || []),
  ];

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.website.trim()) return; // honeypot filled — a bot, not a person; drop silently
    const nameError = validatePersonName(form.name);
    if (nameError) {
      toast.error(nameError);
      return;
    }
    if (form.phone.trim() && !PHONE_PATTERN.test(form.phone.trim())) {
      toast.error(t("contact.phoneInvalid") || "Please enter a valid phone number (digits only).");
      return;
    }
    const messageError = validateMessageText(form.message);
    if (messageError) {
      toast.error(messageError);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", { ...form, language: lang });
      trackEvent("generate_lead", {
        method: "inquiry_form",
        service_type: form.service_type || undefined,
        destination_country: form.destination_country || undefined,
      });
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", service_type: "", destination_country: "", message: "", website: "" });
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error("You're submitting too quickly — please wait a moment and try again.");
        return;
      }
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : t("contact.error");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="inquiry-form">
      {/* Honeypot: invisible to real visitors, off-screen (not display:none, which some
          bots detect and skip) and out of tab order — bots that auto-fill every field trip it. */}
      <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }} aria-hidden="true">
        <label htmlFor="inquiry-website">Website</label>
        <input
          id="inquiry-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={upd("website")}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("contact.name")}</label>
          <input required value={form.name} onChange={upd("name")} className="input-dark" data-testid="inquiry-name" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("contact.email")}</label>
          <input required type="email" value={form.email} onChange={upd("email")} className="input-dark" data-testid="inquiry-email" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("contact.phone")}</label>
          <input
            type="tel"
            inputMode="tel"
            pattern="[0-9+()\-.\s]{6,30}"
            maxLength={30}
            value={form.phone}
            onChange={upd("phone")}
            className="input-dark"
            data-testid="inquiry-phone"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("contact.service")}</label>
          <select value={form.service_type} onChange={upd("service_type")} className="input-dark" data-testid="inquiry-service">
            <option value="">— {t("search.any")} —</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>{loc(s.title, lang)}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("contact.country")}</label>
        <select value={form.destination_country} onChange={upd("destination_country")} className="input-dark" data-testid="inquiry-country">
          <option value="">— {t("search.any")} —</option>
          {countries.map((c) => (
            <option key={c.code} value={c.code}>{c.name}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("contact.message")}</label>
        <textarea
          required
          value={form.message}
          onChange={upd("message")}
          rows={5}
          className="input-dark"
          data-testid="inquiry-message"
        />
      </div>

      <button type="submit" disabled={submitting} className="btn-gold w-full justify-center" data-testid="inquiry-submit">
        {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        {t("contact.submit")}
      </button>
    </form>
  );
}
